import { toFiniteNumber } from "./quantity";

/**
 * Weighted products travel as integer grams end-to-end: the scale label carries
 * grams, the API takes and returns grams. Kilograms exist for display and for
 * the manual-entry field only, so every conversion lives here and rounding
 * happens exactly once — right before the value leaves the browser.
 */

export const GRAMS_PER_KILOGRAM = 1000;

/** Grams below this are shown as grams; at or above it, as kilograms. */
const KILOGRAM_DISPLAY_THRESHOLD = GRAMS_PER_KILOGRAM;

export interface IWeightUnitLabels {
  gram: string;
  kilogram: string;
}

/** Kilograms coming from a user input, rounded to whole grams. */
export const kilogramsToGrams = (kilograms: unknown): number | undefined => {
  const value = toFiniteNumber(kilograms);
  return value === undefined
    ? undefined
    : Math.round(value * GRAMS_PER_KILOGRAM);
};

const formatNumber = (value: number, maximumFractionDigits: number) =>
  value.toLocaleString(undefined, { maximumFractionDigits });

/**
 * `210` -> `210 g`, `13042` -> `13.042 kg`. Returns `"-"` for missing values so
 * callers can render it straight into a table cell.
 */
export const formatWeightFromGrams = (
  grams: unknown,
  labels: IWeightUnitLabels,
): string => {
  const value = toFiniteNumber(grams);
  if (value === undefined) return "-";

  if (Math.abs(value) < KILOGRAM_DISPLAY_THRESHOLD) {
    return `${formatNumber(value, 0)} ${labels.gram}`;
  }

  return `${formatNumber(value / GRAMS_PER_KILOGRAM, 3)} ${labels.kilogram}`;
};

/** Same as above with an explicit sign, for deltas (`+42 g`, `-1.5 kg`). */
export const formatSignedWeightFromGrams = (
  grams: unknown,
  labels: IWeightUnitLabels,
): string => {
  const value = toFiniteNumber(grams);
  if (value === undefined) return "-";

  const formatted = formatWeightFromGrams(Math.abs(value), labels);
  if (value === 0) return formatted;
  return `${value > 0 ? "+" : "-"}${formatted}`;
};

export const isWholeGramAmountInRange = (
  grams: unknown,
  minimum: number,
  maximum: number,
): boolean => {
  const value = toFiniteNumber(grams);
  return (
    value !== undefined &&
    Number.isInteger(value) &&
    value >= minimum &&
    value <= maximum
  );
};
